import { Component, type ErrorInfo, type ReactNode } from 'react';

/* An error boundary around the whole field shell.
 *
 * This matters more here than on the desk. A geologist standing on a ridge with
 * a blank screen has no console to open, no laptop, and possibly no signal —
 * and the thing they most need to know is whether their unsent records are
 * still safe. So the message leads with that, because it is the true and
 * reassuring answer: nothing is lost. Everything captured is in IndexedDB and
 * OPFS on the device, and a render failure cannot touch either.
 *
 * The offer is a reload, not a retry — on a phone the app is a single process
 * and a fresh start is both simpler and more likely to work.
 */

interface Props {
  children: ReactNode;
}

export class Boundary extends Component<Props, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[field]', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="boundary">
        <div className="boundary__t">This screen stopped</div>
        <p className="boundary__s">
          Nothing you captured is lost. Every record and every photo is stored on this
          phone and will send when it can.
        </p>
        <p className="boundary__m mono">{this.state.error.message}</p>
        <button type="button" className="btn btn--primary btn--lg" onClick={() => location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
